import { useEffect, useState, type ReactNode } from 'react';
import { Button, Checkbox, Input, SectionCard, Skeleton, toast } from '@/components/ui';
import { friendlyMessage, friendlyMessageWithDetails } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  TAX_INVOICE_SETTINGS_WRITE_ROLES,
  useSaveTaxInvoiceSettingsMutation,
  useTaxInvoiceSettingsQuery,
  type Supplier,
  type TaxInvoiceSettings,
} from '../api/taxInvoiceApi';
import { formatDateTime } from '../lib/taxInvoiceLib';
import { useTaxInvoiceLabels } from './TaxInvoiceSearchForm';
import { TextArea } from './TextArea';

/*
 * 세금계산서 설정(고도몰 "세금계산서 설정") — 전체 저장(PUT). 전자세금계산서는 2차라 체크를 비활성 "준비 중"으로 둔다
 * (electronicEnabled=true 는 BE 400). 공급자 정보는 인쇄에 쓰인다(고도몰 기본정보에 해당).
 * 저장은 OPS_ADMIN·SUPER_ADMIN, 그 외 조회 권한은 읽기 전용.
 */

const Row = ({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) => (
  <div className="grid grid-cols-1 gap-2 border-b py-3 sm:grid-cols-[180px_1fr]" style={{ borderColor: 'var(--divider-soft)' }}>
    <div className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>{label}</div>
    <div className="flex flex-col gap-1.5 text-sm">
      {children}
      {hint ? <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{hint}</p> : null}
    </div>
  </div>
);

const Radio = ({ name, checked, onChange, disabled, children }: { name: string; checked: boolean; onChange: () => void; disabled: boolean; children: ReactNode }) => (
  <label className="inline-flex items-center gap-1.5">
    <input type="radio" name={name} checked={checked} onChange={onChange} disabled={disabled} />
    {children}
  </label>
);

export const TaxInvoiceSettingsTab = () => {
  const { t } = useTaxInvoiceLabels();
  const query = useTaxInvoiceSettingsQuery();
  const save = useSaveTaxInvoiceSettingsMutation();
  const canSave = useHasRole(TAX_INVOICE_SETTINGS_WRITE_ROLES);
  const [form, setForm] = useState<TaxInvoiceSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) {
      const { updatedAt: _u, updatedBy: _b, ...rest } = query.data;
      setForm(rest);
    }
  }, [query.data]);

  if (query.isPending) return <Skeleton className="h-[400px] w-full" />;
  if (query.isError) {
    return (
      <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
        {friendlyMessage(query.error, t('orders.taxInvoice.settings.loadError', '세금계산서 설정을 불러오지 못했습니다.'))}
      </p>
    );
  }
  if (!form) return null;

  const ro = !canSave;
  const patch = (p: Partial<TaxInvoiceSettings>) => setForm((f) => (f ? { ...f, ...p } : f));
  const patchSupplier = (p: Partial<Supplier>) => setForm((f) => (f ? { ...f, supplier: { ...f.supplier, ...p } } : f));

  const submit = () => {
    setError(null);
    if (form.useEnabled && !form.generalEnabled) {
      return setError(t('orders.taxInvoice.settings.needKind', '사용하려면 발행 종류(일반 세금계산서)를 선택해 주세요.'));
    }
    if (form.requestLimitEnabled && (form.requestLimitDay < 1 || form.requestLimitDay > 28)) {
      return setError(t('orders.taxInvoice.settings.limitDay', '신청 기한은 1~28일 중에서 정해 주세요.'));
    }
    const bn = form.supplier.businessNo.replace(/\D/g, '');
    if (bn && bn.length !== 10) {
      return setError(t('orders.taxInvoice.settings.businessNo', '공급자 사업자번호 10자리를 확인해 주세요.'));
    }
    // 전자세금계산서는 2차 — 항상 false 로 저장(BE 가 true 를 400 으로 막는다).
    save.mutate(
      { ...form, electronicEnabled: false },
      {
        onSuccess: () => toast.success(t('orders.taxInvoice.settings.saved', '세금계산서 설정을 저장했습니다.')),
        onError: (e) => setError(friendlyMessageWithDetails(e, t('orders.taxInvoice.settings.saveFail', '설정을 저장하지 못했습니다.'))),
      },
    );
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      data-testid="tax-invoice-settings"
    >
      <SectionCard title={t('orders.taxInvoice.settings.basic', '기본 설정')}>
        <Row label={t('orders.taxInvoice.settings.use', '사용 설정')} hint={t('orders.taxInvoice.settings.useHint', '사용하면 고객이 무통장입금 주문에서 세금계산서를 신청할 수 있습니다.')}>
          <div className="flex gap-4">
            <Radio name="ti-use" checked={form.useEnabled} onChange={() => patch({ useEnabled: true })} disabled={ro}>{t('orders.taxInvoice.settings.on', '사용함')}</Radio>
            <Radio name="ti-use" checked={!form.useEnabled} onChange={() => patch({ useEnabled: false })} disabled={ro}>{t('orders.taxInvoice.settings.off', '사용안함')}</Radio>
          </div>
        </Row>
        <Row label={t('orders.taxInvoice.settings.kinds', '발행 종류')}>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-1.5">
              <Checkbox checked={form.generalEnabled} onCheckedChange={(v) => patch({ generalEnabled: v === true })} disabled={ro} data-testid="ti-general" />
              {t('orders.taxInvoice.settings.general', '일반 세금계산서')}
            </label>
            <label className="inline-flex items-center gap-1.5" style={{ color: 'var(--content-muted)' }} title={t('orders.taxInvoice.electronicSoon', '전자세금계산서는 준비 중입니다.')}>
              <Checkbox checked={false} disabled data-testid="ti-electronic" />
              {t('orders.taxInvoice.settings.electronic', '전자 세금계산서 (준비 중)')}
            </label>
          </div>
        </Row>
        <Row label={t('orders.taxInvoice.settings.limit', '신청 기간 제한')} hint={t('orders.taxInvoice.settings.limitHint', '켜면 결제한 달의 다음 달 N일까지만 신청할 수 있습니다.')}>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-1.5">
              <Checkbox checked={form.requestLimitEnabled} onCheckedChange={(v) => patch({ requestLimitEnabled: v === true })} disabled={ro} />
              {t('orders.taxInvoice.settings.limitOn', '제한함')}
            </label>
            <span className="inline-flex items-center gap-1.5">
              {t('orders.taxInvoice.settings.nextMonth', '다음 달')}
              <Input type="number" min={1} max={28} className="w-20" value={form.requestLimitDay} disabled={ro || !form.requestLimitEnabled} onChange={(e) => patch({ requestLimitDay: Number(e.target.value) })} aria-label={t('orders.taxInvoice.settings.limitDayLabel', '신청 기한 일')} />
              {t('orders.taxInvoice.settings.dayUntil', '일까지')}
            </span>
          </div>
        </Row>
        <Row label={t('orders.taxInvoice.settings.include', '발행 금액에 포함')} hint={t('orders.taxInvoice.settings.includeHint', '체크한 금액을 발행 금액에 넣습니다.')}>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-1.5"><Checkbox checked={form.includeShipping} onCheckedChange={(v) => patch({ includeShipping: v === true })} disabled={ro} />{t('orders.taxInvoice.settings.shipping', '배송비')}</label>
            <label className="inline-flex items-center gap-1.5"><Checkbox checked={form.includeMileage} onCheckedChange={(v) => patch({ includeMileage: v === true })} disabled={ro} />{t('orders.taxInvoice.settings.mileage', '마일리지 사용분')}</label>
            <label className="inline-flex items-center gap-1.5"><Checkbox checked={form.includeDeposit} onCheckedChange={(v) => patch({ includeDeposit: v === true })} disabled={ro} />{t('orders.taxInvoice.settings.deposit', '예치금 사용분')}</label>
          </div>
        </Row>
        <Row label={t('orders.taxInvoice.settings.issueDateBasis', '발행일 기준')} hint={t('orders.taxInvoice.settings.issueDateBasisHint', '발행 기록 시 발행일을 비우면 이 날짜를 씁니다.')}>
          <div className="flex gap-4">
            <Radio name="ti-basis" checked={form.issueDateBasis === 'PAYMENT'} onChange={() => patch({ issueDateBasis: 'PAYMENT' })} disabled={ro}>{t('orders.taxInvoice.settings.paymentDate', '결제완료일')}</Radio>
            <Radio name="ti-basis" checked={form.issueDateBasis === 'DELIVERY'} onChange={() => patch({ issueDateBasis: 'DELIVERY' })} disabled={ro}>{t('orders.taxInvoice.settings.deliveryDate', '배송완료일')}</Radio>
          </div>
        </Row>
        <Row label={t('orders.taxInvoice.settings.checkout', '주문서 노출')}>
          <label className="inline-flex items-center gap-1.5">
            <Checkbox checked={form.showOnCheckout} onCheckedChange={(v) => patch({ showOnCheckout: v === true })} disabled={ro} />
            {t('orders.taxInvoice.settings.checkoutOn', '주문서에서 세금계산서 신청 받기')}
          </label>
        </Row>
        <Row label={t('orders.taxInvoice.settings.guide', '신청 안내 문구')}>
          <label className="inline-flex items-center gap-1.5">
            <Checkbox checked={form.guideEnabled} onCheckedChange={(v) => patch({ guideEnabled: v === true })} disabled={ro} />
            {t('orders.taxInvoice.settings.show', '노출')}
          </label>
          <TextArea value={form.guideText} maxLength={2000} disabled={ro || !form.guideEnabled} onChange={(e) => patch({ guideText: e.target.value })} aria-label={t('orders.taxInvoice.settings.guide', '신청 안내 문구')} />
        </Row>
        <Row label={t('orders.taxInvoice.settings.deadlineGuide', '신청 기한 안내 문구')}>
          <label className="inline-flex items-center gap-1.5">
            <Checkbox checked={form.deadlineGuideEnabled} onCheckedChange={(v) => patch({ deadlineGuideEnabled: v === true })} disabled={ro} />
            {t('orders.taxInvoice.settings.show', '노출')}
          </label>
          <TextArea value={form.deadlineGuideText} maxLength={2000} disabled={ro || !form.deadlineGuideEnabled} onChange={(e) => patch({ deadlineGuideText: e.target.value })} aria-label={t('orders.taxInvoice.settings.deadlineGuide', '신청 기한 안내 문구')} />
        </Row>
      </SectionCard>

      <SectionCard title={t('orders.taxInvoice.settings.supplier', '공급자 정보')} description={t('orders.taxInvoice.settings.supplierHint', '세금계산서 인쇄의 공급자 칸에 쓰입니다.')}>
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          {([
            ['companyName', t('orders.taxInvoice.col.company', '회사명'), 100],
            ['businessNo', t('orders.taxInvoice.col.businessNo', '사업자번호'), 20],
            ['ceoName', t('orders.taxInvoice.col.ceo', '대표자'), 50],
            ['businessType', t('orders.taxInvoice.col.businessType', '업태'), 100],
            ['businessItem', t('orders.taxInvoice.col.businessItem', '종목'), 100],
            ['address', t('orders.taxInvoice.col.address', '사업장 주소'), 255],
          ] as const).map(([key, label, max]) => (
            <label key={key} className={`flex flex-col gap-1 ${key === 'address' ? 'sm:col-span-2' : ''}`}>
              {label}
              <Input value={form.supplier[key]} maxLength={max} disabled={ro} onChange={(e) => patchSupplier({ [key]: e.target.value })} data-testid={`ti-supplier-${key}`} />
            </label>
          ))}
        </div>
      </SectionCard>

      {error ? <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>{error}</p> : null}
      <div className="flex items-center justify-end gap-3">
        {query.data?.updatedAt ? (
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {t('orders.taxInvoice.settings.lastSaved', { defaultValue: '마지막 저장 {{at}}', at: formatDateTime(query.data.updatedAt) })}
            {query.data.updatedBy ? ` · ${query.data.updatedBy}` : ''}
          </span>
        ) : null}
        {canSave ? (
          <Button type="submit" variant="primary" disabled={save.isPending} data-testid="ti-settings-save">
            {t('orders.taxInvoice.save', '저장')}
          </Button>
        ) : (
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{t('orders.taxInvoice.settings.readOnly', '설정 저장은 운영 관리자만 할 수 있습니다.')}</span>
        )}
      </div>
    </form>
  );
};
